import axios, { isAxiosError } from "axios";
import { API_BASE_URL, normalizeApiBaseUrl } from "../constants";
import { signWidgetMintJwt, canSignWidgetJwt } from "../utils/widgetJwt";
const DEFAULT_ISS = "booking-widget";
function widgetSigningConfig() {
    return {
        kid: process.env.WIDGET_JWT_KID ?? "",
        privateKey: process.env.WIDGET_JWT_PRIVATE_KEY ?? "",
    };
}
let sessionToken = null;
let sessionExpiresIn = null;
let geoLocation = null;
let sessionWorkspaces = [];
const AuthService = {
    async init(apiKey, baseUrl = API_BASE_URL, options) {
        const headers = await this.buildMintHeaders(apiKey, options?.iss);
        try {
            const url = `${normalizeApiBaseUrl(baseUrl)}/auth/session`;
            const res = await axios.post(url, undefined, { headers });
            const token = res.data?.access_token;
            if (!token || typeof token !== "string") {
                throw new Error("Invalid session token response");
            }
            sessionToken = token;
            sessionExpiresIn = res.data?.expires_in ?? null;
            const workspaces = res.data?.workspaces;
            sessionWorkspaces = Array.isArray(workspaces)
                ? workspaces
                : Array.isArray(workspaces?.list)
                    ? workspaces.list
                    : [];
            const geo = res.data?.geo_location;
            const geoCountry = geo?.country?.trim().toUpperCase();
            geoLocation =
                geo && geoCountry
                    ? {
                        country: geoCountry,
                        countryName: geo.country_name?.trim() || null,
                        timezone: geo.timezone?.trim() || null,
                    }
                    : null;
            return token;
        }
        catch (e) {
            if (isAxiosError(e) && e.response) {
                const status = e.response.status;
                const serverMessage = e.response.data?.message || e.response.data?.error || "";
                if (status === 400) {
                    throw new Error(serverMessage || "API key is required");
                }
                if (status === 401) {
                    throw new Error(serverMessage || "Invalid API key");
                }
                if (status === 403) {
                    throw new Error(serverMessage ||
                        "This domain is not authorized for this API key. Add it to the key's allowed origin.");
                }
                if (status === 429) {
                    throw new Error(serverMessage || "Too many requests. Please try again shortly.");
                }
            }
            throw new Error(`Failed to initialize session: ${e.message}`);
        }
    },
    async buildMintHeaders(apiKey, iss) {
        const { kid, privateKey } = widgetSigningConfig();
        if (!kid || !privateKey) {
            throw new Error("Widget mint signing material is missing — the bundle was built without " +
                "WIDGET_JWT_KID / WIDGET_JWT_PRIVATE_KEY.");
        }
        const origin = typeof window !== "undefined" ? window.location?.origin : undefined;
        if (!origin) {
            throw new Error("Widget mint requires a browser origin. Outside the browser, mint " +
                "server-side and use MedosClient.initWithSession({ sessionToken }).");
        }
        if (!canSignWidgetJwt()) {
            throw new Error("Web Crypto is unavailable — cannot sign the widget mint JWT.");
        }
        const jwt = await signWidgetMintJwt({ apiKey, kid, iss: iss || DEFAULT_ISS, origin }, privateKey);
        return { Authorization: `Bearer ${jwt}` };
    },
    getToken() {
        return sessionToken;
    },
    getExpiresIn() {
        return sessionExpiresIn;
    },
    getGeoLocation() {
        return geoLocation;
    },
    getSessionWorkspaces() {
        return sessionWorkspaces;
    },
    getSessionWorkspace(workspaceId) {
        if (workspaceId === null || workspaceId === undefined)
            return null;
        const id = Number(workspaceId);
        if (!Number.isFinite(id))
            return null;
        return sessionWorkspaces.find((workspace) => workspace.id === id) ?? null;
    },
    clear() {
        sessionToken = null;
        sessionExpiresIn = null;
        geoLocation = null;
        sessionWorkspaces = [];
    },
};
export { AuthService };
